import DescriptionIcon from '@mui/icons-material/Description'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { Toolbar, useGetList, useNotify, useRefresh, useTranslate } from 'react-admin'
import { ZaakDocument } from '../types/apiRequests'
import { getUrl, httpClient } from '../providers/data'

interface RequestDocumentsDialogProps {
  actieverzoekId?: string
  samenwerkingId?: string
  linkedDocumentIds?: string[]
  currentStatus?: string | null
  samenwerkingTitel?: string | null
  actieverzoekTitel?: string | null
  renderTrigger: (open: () => void) => ReactNode
}

const RequestDocumentsDialog: FC<RequestDocumentsDialogProps> = ({
  actieverzoekId,
  samenwerkingId,
  linkedDocumentIds = [],
  currentStatus,
  samenwerkingTitel,
  actieverzoekTitel,
  renderTrigger,
}) => {
  const [open, setOpen] = useState(false)
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()

  const { data: samenwerkingDocumenten, isLoading: isLoadingDocumenten } = useGetList<ZaakDocument>(
    `documenten/${samenwerkingId}`,
    { filter: {}, pagination: { page: 1, perPage: 200 } },
    { enabled: open && !!samenwerkingId }
  )

  useEffect(() => {
    if (!open) return
    setSelectedDocumentIds(linkedDocumentIds)
  }, [open, linkedDocumentIds])

  const toggleDocument = (documentId: string) => {
    setSelectedDocumentIds(current =>
      current.includes(documentId)
        ? current.filter(id => id !== documentId)
        : [...current, documentId]
    )
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const isReadOnly = ['GEWEIGERD', 'INGETROKKEN', 'GEREED', 'GEREEDGEMELD'].includes(currentStatus ?? '')

  const handleSave = async () => {
    setIsSaving(true)
    try {
      if (!actieverzoekId) {
        return
      }
      await httpClient(`${getUrl()}/actieverzoeken/${actieverzoekId}/documenten`, {
        method: 'PUT',
        body: JSON.stringify({ documenten: selectedDocumentIds }),
      })
      notify('ra.notification.updated', { type: 'info' })
      setOpen(false)
      refresh()
    } catch (saveError) {
      const apiError = saveError as {
        message?: string
        body?: { detail?: string; message?: string }
      }
      notify(
        apiError?.body?.detail ||
        apiError?.body?.message ||
        apiError?.message ||
        'ra.notification.http_error',
        { type: 'warning' }
      )
    } finally {
      setIsSaving(false)
    }
  }

  const sortedDocumenten = useMemo(
    () => [...(samenwerkingDocumenten ?? [])]
      .filter(doc => doc.bron === 'dso' || doc.synced)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [samenwerkingDocumenten]
  )

  return (
    <>
      {renderTrigger(handleOpen)}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {translate('data.linkDocument')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{samenwerkingTitel}</Typography>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>{actieverzoekTitel}</Typography>

          {isLoadingDocumenten ? (
            <Typography variant="body2">{translate('ra.message.loading')}</Typography>
          ) : (
            <>
              {sortedDocumenten.length > 0 ? (
                <Box
                  sx={{
                    maxHeight: '500px',
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {sortedDocumenten.map((doc, index) => {
                    const dsoId = doc.bron === 'dso' ? doc.id : doc.dso_document_id
                    return (
                      <Box key={doc.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Typography variant="body2">{doc.name}</Typography>
                          <Checkbox
                            checked={!!dsoId && selectedDocumentIds.includes(dsoId)}
                            disabled={isReadOnly || !dsoId}
                            onChange={() => dsoId && toggleDocument(dsoId)}
                          />
                        </Box>
                        {index < sortedDocumenten.length - 1 && <Divider />}
                      </Box>
                    )
                  })}
                </Box>
              ) : (
                <Box sx={{ minHeight: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('show.noDocuments')}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {translate('data.linkDocumentDsoOnly')}
              </Typography>
              {isReadOnly && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {translate('data.requestStatusReadonly')}
                </Typography>
              )}
            </>
          )}

          <Toolbar>
            <Button onClick={handleClose}>{translate('ra.action.cancel')}</Button>
            <Button
              variant="contained"
              onClick={() => { void handleSave() }}
              disabled={isSaving || isReadOnly}
            >
              {translate('ra.action.save')}
            </Button>
          </Toolbar>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default RequestDocumentsDialog
